/** Marketing i18n fragment — intgJira (integrations/jira). */

export const intgJiraEn = {
  meta: {
    title: "Jira integration — issue read, transitions, labels and approved replies",
    description:
      "FixControl reads and searches Jira issues, transitions status, applies labels, and writes internal comments under approval. On Jira Service Management projects it can also send approved customer replies. Health is tracked per capability, and writes go through a retried outbox.",
  },
  hero: {
    eyebrow: "Jira integration",
    title: "Read for context,",
    highlight: "write with approval",
    subtitle:
      "FixControl reads and searches issues to answer questions and assemble evidence. Transitions, labels, internal comments, and JSM customer replies are proposed first and dispatched only after an approval is recorded.",
    primaryCta: "Book a demo",
    secondaryCta: "See ticket questions",
    bullet1: "JSM vs. Jira Software",
    bullet2: "Approved replies only",
    bullet3: "Capability health, probed",
  },
  whatItDoes: {
    eyebrow: "What it does",
    title: "What FixControl does with Jira issues",
  },
  features: {
    read: {
      title: "Issue read & search",
      body: "Reads and searches issues across your projects to answer ticket questions and assemble evidence. Access stays inside the bound project and tenant.",
    },
    transitions: {
      title: "Status transitions",
      body: "Moves an issue through its workflow as part of an approved action. A transition only runs if the configured credentials are allowed to perform it in that workflow.",
    },
    labels: {
      title: "Labels",
      body: "Applies or updates labels to keep triage, routing, and reporting consistent. Each label change is an attributed, recorded write.",
    },
    comments: {
      title: "Internal comments",
      body: "Writes internal comments visible to engineers and agents. Requires the connection to have comment-write scope.",
    },
    replies: {
      title: "JSM customer replies",
      body: "On a Jira Service Management customer request, FixControl can send a customer-visible reply — when the integration is configured and the customer-reply capability has probed healthy. The reply is drafted by AI, run through reply-safety checks, approved by someone on your team, then posted through the Service Management API via the outbox. Jira Software projects have no customer-reply path; notes there stay internal comments.",
    },
    audit: {
      title: "Evidence & audit",
      body: "Each write is recorded with the actor, role, and evidence shown at approval time, so the issue history and the decision ledger tell the same story.",
    },
  },
  capabilitiesSection: {
    eyebrow: "Capabilities & health",
    title: "Connected doesn’t mean everything works",
    description:
      "A Jira connection can read and search issues while comment write or customer reply is unavailable — scopes and project type decide. FixControl probes each capability and shows its health separately.",
  },
  capabilities: {
    read: {
      name: "Read & search issues",
      body: "Reads and searches issues within the bound project and tenant, for answering questions and assembling evidence.",
    },
    transitions: {
      name: "Status transitions",
      body: "Runs where the transition is valid in the issue’s workflow and permitted for the configured credentials.",
    },
    labels: {
      name: "Apply / update labels",
      body: "Applied as recorded, attributed writes through the outbox.",
    },
    comments: {
      name: "Internal comment write",
      body: "Needs comment-write scope on the connection. Internal comments stay visible to agents and engineers only; customers don’t see them.",
    },
    replies: {
      name: "JSM customer-visible reply",
      body: "Exists only on Jira Service Management projects. Jira Software has no customer-reply path, so the capability shows unavailable there.",
    },
    singleStatus: {
      name: "One green checkmark",
      body: "Not something FixControl reports. Read can be healthy while comment write or customer reply isn’t, and one checkmark would hide that.",
    },
  },
  failures: {
    eyebrow: "Failed writes",
    title: "A failed transition doesn’t vanish",
  },
  securityNote: {
    title: "The outbox behind every Jira write",
    body: "Transitions, labels, comments, and JSM replies dispatch through the integration outbox: transient errors retry, persistent ones dead-letter, and the failure appears on the operational timeline. The approval that authorized the write is stored in the decision ledger with actor, role, channel, evidence, and consequence.",
  },
  faq: {
    eyebrow: "FAQ",
    title: "Questions about the Jira integration",
    q1: {
      q: "Does FixControl reply to customers on every Jira project?",
      a: "No. Customer-visible replies exist only on Jira Service Management projects. On Jira Software, FixControl reads and searches issues, transitions status, applies labels, and writes internal comments — there is no path to a customer.",
    },
    q2: {
      q: "What’s the difference between an internal comment and a customer reply?",
      a: "An internal comment is visible to agents and engineers and needs comment-write scope. A JSM customer reply is customer-visible and exists only on Service Management projects. FixControl treats them as distinct capabilities and won’t post one as the other.",
    },
    q3: {
      q: "Does AI change Jira state on its own?",
      a: "No. AI proposes the transition, label, comment, or reply; the write dispatches only after a person approves it. The approval is recorded with role and evidence first.",
    },
    q4: {
      q: "What happens if a Jira write fails?",
      a: "A failed transition or comment stays in the outbox and retries. If it keeps failing it’s dead-lettered and flagged on the timeline, so the issue is not left looking updated when it isn’t.",
    },
    q5: {
      q: "Are Jira actions audited?",
      a: "Yes. Each governed Jira write is entered in the decision ledger alongside the approval that authorized it, so the ledger and the issue history match.",
    },
  },
  related: {
    ticketQuestions: {
      label: "Ticket questions",
      description: "Query open Jira work from Slack or Teams.",
    },
    approvedReplies: {
      label: "Approved replies",
      description: "Drafted by AI, sent only after a person approves — Freshdesk and JSM.",
    },
    slack: {
      label: "Slack",
      description: "Approve Jira actions from a card in the thread.",
    },
  },
  cta: {
    title: "Watch a Jira transition go through approval",
    description:
      "Book a demo: issue read, transitions, labels, internal comments, and an approved JSM reply — with the ledger entries to match.",
    primary: "Book a demo",
    secondary: "Ask FixControl",
  },
};

export const intgJiraNl = {
  meta: {
    title: "Jira-integratie — gegoverneerd issues lezen, statusovergangen, labels en antwoorden",
    description:
      "Koppel Jira aan FixControl voor gegoverneerd issues lezen/doorzoeken, statusovergangen, labels en interne opmerkingen — en, op Jira Service Management-projecten, goedgekeurde klant-zichtbare antwoorden. Capability-health, niet één groen vinkje.",
  },
  hero: {
    eyebrow: "Jira-integratie",
    title: "Lezen voor context,",
    highlight: "schrijven met goedkeuring",
    subtitle:
      "FixControl leest en doorzoekt issues om vragen te beantwoorden en bewijs samen te stellen. Statusovergangen, labels, interne opmerkingen en JSM-klantantwoorden worden eerst voorgesteld en pas verstuurd nadat een goedkeuring is vastgelegd.",
    primaryCta: "Boek een demo",
    secondaryCta: "Bekijk ticketvragen",
    bullet1: "JSM vs. Jira Software",
    bullet2: "Alleen goedgekeurde antwoorden",
    bullet3: "Capability-health",
  },
  whatItDoes: {
    eyebrow: "Wat het doet",
    title: "Wat FixControl met Jira-issues doet",
  },
  features: {
    read: {
      title: "Issues lezen & doorzoeken",
      body: "Lees en doorzoek issues over je projecten heen om ticketvragen te beantwoorden en bewijs samen te stellen — gescoped tot het gekoppelde project en de tenant.",
    },
    transitions: {
      title: "Statusovergangen",
      body: "Beweeg een issue door zijn workflow als onderdeel van een gegoverneerde actie, waar de overgang is toegestaan voor de geconfigureerde credentials.",
    },
    labels: {
      title: "Labels",
      body: "Pas labels toe of werk ze bij zodat triage, routering en rapportage consistent blijven — als een vastgelegde, toewijsbare actie.",
    },
    comments: {
      title: "Interne opmerkingen",
      body: "Schrijf interne opmerkingen op een issue voor engineers en agents, waar de koppeling comment-write-scope heeft.",
    },
    replies: {
      title: "JSM klantantwoorden",
      body: "Op een ondersteund Jira Service Management-klantverzoek — met de integratie geconfigureerd en de klantantwoord-capability als gezond gemeten — kan een klant-zichtbaar antwoord worden verstuurd: opgesteld door AI, reply-safety-gecontroleerd, goedgekeurd door iemand van je team, daarna geplaatst via de Service Management-API en de gegoverneerde outbox. Dit bestaat niet op Jira Software-projecten, waar notities interne opmerkingen blijven.",
    },
    audit: {
      title: "Bewijs & audit",
      body: "Elke gegoverneerde schrijfactie wordt vastgelegd met actor, rol en het getoonde bewijs, zodat de issue-historie en het beslis-grootboek overeenkomen.",
    },
  },
  capabilitiesSection: {
    eyebrow: "Capabilities & health",
    title: "Verbonden betekent niet dat alles werkt",
    description:
      "Een Jira-koppeling kan issues lezen en doorzoeken terwijl comment-write of klantantwoord onbeschikbaar is — scopes en projecttype bepalen dat. FixControl test elke capability en toont zijn health afzonderlijk.",
  },
  capabilities: {
    read: {
      name: "Issues lezen & doorzoeken",
      body: "FixControl leest en doorzoekt issues binnen het gekoppelde project en de tenant om vragen te beantwoorden en bewijs samen te stellen.",
    },
    transitions: {
      name: "Statusovergangen",
      body: "Ondersteund waar de overgang geldig is voor de workflow van het issue en de geconfigureerde credentials hem mogen uitvoeren.",
    },
    labels: {
      name: "Labels toepassen / bijwerken",
      body: "Labels worden toegepast als een vastgelegde, toewijsbare actie via de outbox, met retry- en dead-letter-afhandeling bij falen.",
    },
    comments: {
      name: "Interne opmerking schrijven",
      body: "Vereist dat de koppeling comment-write-scope heeft. Interne opmerkingen zijn zichtbaar voor agents en engineers, niet voor de klant.",
    },
    replies: {
      name: "JSM klant-zichtbaar antwoord",
      body: "Alleen beschikbaar op Jira Service Management-projecten. Er is geen klantantwoord-pad op Jira Software-projecten, dus deze capability is daar niet beschikbaar.",
    },
    singleStatus: {
      name: "Eén groen vinkje",
      body: "Niet iets dat FixControl rapporteert. Lezen kan gezond zijn terwijl comment-write of klantantwoord dat niet is, en één vinkje zou dat verbergen.",
    },
  },
  failures: {
    eyebrow: "Mislukte schrijfacties",
    title: "Een mislukte overgang verdwijnt niet",
  },
  securityNote: {
    title: "De outbox achter elke Jira-schrijfactie",
    body: "Statusovergangen, labels, opmerkingen en JSM-antwoorden lopen via de integratie-outbox: tijdelijke fouten worden opnieuw geprobeerd, hardnekkige belanden in de dead-letter, en de fout verschijnt op de operationele tijdlijn. De goedkeuring die de schrijfactie autoriseerde staat in het beslis-grootboek met actor, rol, kanaal, bewijs en consequentie.",
  },
  faq: {
    eyebrow: "FAQ",
    title: "Veelgestelde vragen",
    q1: {
      q: "Antwoordt FixControl klanten op elk Jira-project?",
      a: "Nee. Klant-zichtbare antwoorden bestaan alleen op Jira Service Management-projecten. Op Jira Software-projecten is er geen klantantwoord-pad — FixControl kan issues lezen/doorzoeken, status laten overgaan, labels toepassen en interne opmerkingen schrijven, maar niet een klant antwoorden.",
    },
    q2: {
      q: "Wat is het verschil tussen een interne opmerking en een klantantwoord?",
      a: "Een interne opmerking is zichtbaar voor agents en engineers en vereist comment-write-scope. Een JSM klantantwoord is klant-zichtbaar en bestaat alleen op Jira Service Management-projecten. FixControl behandelt ze als aparte capabilities en plaatst de een nooit als de ander.",
    },
    q3: {
      q: "Verandert AI de Jira-status uit zichzelf?",
      a: "Nee. AI stelt de overgang, het label, de opmerking of het antwoord voor; een mens keurt het goed. De goedkeuring wordt vastgelegd met rol en bewijs voordat de schrijfactie wordt verstuurd.",
    },
    q4: {
      q: "Wat gebeurt er als een Jira-schrijfactie faalt?",
      a: "Uitgaande schrijfacties lopen via de integratie-outbox met retry- en dead-letter-afhandeling, en de fout is zichtbaar op de tijdlijn in plaats van stilletjes te verdwijnen.",
    },
    q5: {
      q: "Worden Jira-acties geaudit?",
      a: "Ja. Elke gegoverneerde Jira-schrijfactie wordt naar het beslis-grootboek geschreven met de actor, rol, kanaal, het getoonde bewijs en de consequentie, zodat de issue-historie en het grootboek overeenkomen.",
    },
  },
  related: {
    ticketQuestions: {
      label: "Ticketvragen",
      description: "Bevraag openstaand Jira-werk vanuit Slack of Teams.",
    },
    approvedReplies: {
      label: "Goedgekeurde antwoorden",
      description: "Door AI opgesteld, door een mens goedgekeurd — Freshdesk en JSM.",
    },
    slack: {
      label: "Slack",
      description: "Keur Jira-acties goed vanaf een kaart in de thread.",
    },
  },
  cta: {
    title: "Zie een Jira-overgang door de goedkeuring gaan",
    description:
      "Boek een demo: issues lezen, statusovergangen, labels, interne opmerkingen en een goedgekeurd JSM-antwoord — met de bijbehorende grootboekregels.",
    primary: "Boek een demo",
    secondary: "Vraag het FixControl",
  },
};
